import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

import { userLoginAsAgencyUser, userMe } from "@/store/auth";
import { usersUpdateUser, usersDeleteUser } from "@/store/users";

import Register from "@/pages/client/Register";

import AppModal from "@/components/AppModal";
import AppBox from "@/components/AppBox";
import AppButton from "@/components/AppButton";
import AgencyAdminTable from "@/components/Tables/AgencyAdminTable";
import AppPageContainer from "@/components/AppPageContainer";
import AppInput from "@/components/AppInput";
import { useDebounce } from "@/hooks/useDebounce";

const AgencyDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { me } = useAppSelector((state) => state.auth);

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");

  // Debounce search input to avoid filtering on each keystroke immediately
  const debouncedSearch = useDebounce(inputValue, 300);

  const handleToggleAddUserModal = () => {
    setIsOpen((prev) => !prev);
  };

  const handleLoginUnderUser = async (user: { id: number }) => {
    await dispatch(userLoginAsAgencyUser( user.id));
    dispatch(userMe());
    navigate("/");
  };

  const handleAskToDeleteUser = async (user: { id: number; name: string }) => {
    if (window.confirm(`Are you sure you want to delete ${user.name} user?`)) {
      handleDeleteUser(user);
    }
  };

  const handleDeleteUser = async (user: { id: number }) => {
    await dispatch(usersDeleteUser(user.id));
    dispatch(userMe());
  };

  const handleChangeField = async (
    fieldName: string,
    user: { id: number; [key: string]: any }
  ) => {
    const newData = { ...user, [fieldName]: !user[fieldName] };
    await dispatch(usersUpdateUser({ id: user.id, data: newData }));
    await dispatch(userMe());
  };

  const handleSubmit = () => {
    dispatch(userMe());
    handleToggleAddUserModal();
  }

  // Prepare table rows with local search filtering
  const filteredUsers = me.agency?.users
    ? me.agency.users
        .filter((user: any) => !user.is_agency_admin)
        .filter((user: any) => {
          if (!debouncedSearch) return true;
          const search = debouncedSearch.toLowerCase();
          return (
            user.company?.toLowerCase().includes(search) ||
            user.name?.toLowerCase().includes(search) ||
            user.customer_id?.toLowerCase().includes(search)
          );
        })
        // Sort by created_at so the most recent users appear first
        .sort(
          (a: any, b: any) =>
            new Date(b.created_at).valueOf() - new Date(a.created_at).valueOf()
        )
    : [];

  return (
    <AppPageContainer
      title={`Agency (${me.agency?.name})`}
      titleAdorment={
        <AppBox align="center" gap={4}>
          <AppInput
            placeholder="Search Users"
            value={inputValue}
            onChange={(value) => setInputValue(value)}
            className="w-[320px]"
          />
          <AppButton onClick={handleToggleAddUserModal}>Add User</AppButton>
        </AppBox>
      }  
    >
      <AgencyAdminTable
        tableRows={filteredUsers}
        onLoginUnderUser={handleLoginUnderUser}
        onDelete={handleAskToDeleteUser}
        onChange={handleChangeField}
      />
      
      <AppModal isOpen={isOpen} title={"Add User"} onClose={handleToggleAddUserModal}>
        <Register agency={me.agency} onSubmit={handleSubmit} />
      </AppModal>
    </AppPageContainer>
  );
};

export default AgencyDashboard;